import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Popup, NumberBox, SelectBox, TextBox } from "devextreme-react";
import { LoadPanel } from "devextreme-react/load-panel";
import axios from "axios";
import { alert } from "devextreme/ui/dialog";
import { formatNumber } from "devextreme/localization";
import DynamicFromLookup from "../../../../../dynamicFormBuilder/DynamicFromLookup";
import { MESSAGES } from "../../../../../common/Messages";
import routes from "../../../../../assets/apiRoutes.json";
import { DynamicLookupOptions } from "../../../../../enum/Enum";
import "../../../../../dynamicFormBuilder/formField.css";

// Split Deposit dialog — port of Delphi uOMD0035 (OM Split Deposit).
// Transfers a deposit held on a parent sales order onto the current order.
const emptyGuid = "00000000-0000-0000-0000-000000000000";
const baseUrl = () => window._env_?.REACT_APP_APIS || "";
const round2 = (value) => Math.round((Number(value) || 0) * 100) / 100;
const hasGuid = (value) => !!value && value !== emptyGuid;

// Basis for the Amount <-> Percent conversion (Delphi edtBasis Codes 'P'/'C').
const BASIS = { PARENT: "P", CURRENT: "C" };
const BASIS_OPTIONS = [
    { id: BASIS.PARENT, name: "Based on Parent Avl Deposit" },
    { id: BASIS.CURRENT, name: "Based on Current Trans Total" },
];

const CURRENCY_FORMAT = "#,##0.00";
// Delphi FloatToStrf(..., ffCurrency, 15, 2) in the Balance Due message.
const MESSAGE_CURRENCY_FORMAT = { type: "currency", precision: 2, currency: "USD", useGrouping: true };

// Parent-order picker: standard TransactionHeader lookup (dropdown + grid popup) restricted to orders holding a deposit.
// Columns follow the Delphi lookup's criteria fields (edtOrderNoParent CriteriaFields).
const PARENT_ORDER_LOOKUP_LAYOUT = {
    valueExpr: "RowId",
    displayExpr: "TransactionNumber",
    sort: [{ selector: "TransactionNumber", desc: false }],
    searchEnabled: true,
    acceptCustomValue: false,
    gridKey: "B0C7F1A2-3D45-4E6F-8A90-1B2C3D4E5F60",
    params: { module: "OM", lookupType: "TransactionHeader", depositOnly: true, showLookupGrid: true, isPascalCase: true },
    columns: [
        { dataField: "Record", caption: "Record #", dataType: "number" },
        { dataField: "TransactionNumber", caption: "Order #", dataType: "string" },
        { dataField: "TransactionDate", caption: "Trans Date", dataType: "date" },
        { dataField: "CustomerIdentityId", caption: "Customer", dataType: "string" },
        { dataField: "CustomerName", caption: "Customer Name", dataType: "string" },
        { dataField: "ShipToId", caption: "Ship To", dataType: "string" },
        { dataField: "ProfileCode", caption: "Profile", dataType: "string" },
    ],
};

// Receipt-method picker: standard AC AcReceiptMethodSetups lookup (dropdown + grid popup).
const RECEIPT_METHOD_LOOKUP_LAYOUT = {
    valueExpr: "id",
    displayExpr: "receiptmethod",
    sort: [{ selector: "receiptmethod", desc: false }],
    searchEnabled: true,
    acceptCustomValue: false,
    gridKey: "C1D8E2B3-4E56-4F70-9B01-2C3D4E5F6071",
    params: { module: "AC", lookupType: "AcReceiptMethodSetups", showLookupGrid: true },
    columns: [
        { dataField: "receiptmethod", caption: "Receipt Method", dataType: "string" },
        { dataField: "description", caption: "Description", dataType: "string" },
    ],
};

const focusEditor = (ref) => {
    const instance = ref.current?.instance;
    const editor = typeof instance === "function" ? instance.call(ref.current) : instance;
    if (editor) editor.focus();
};

function SplitDepositPopup(props) {
    const { showPopup = {}, setShowPopup, accessToken, onSuccess } = props;
    const visible = showPopup.visible === true;
    const headerRowId = showPopup.headerRowId;

    const [loading, setLoading] = useState(false);
    const [current, setCurrent] = useState({});
    const [parent, setParent] = useState(null);
    const [parentLocked, setParentLocked] = useState(false);
    const [receiptMethodRowId, setReceiptMethodRowId] = useState(null);
    const [basis, setBasis] = useState(BASIS.CURRENT);
    const [amount, setAmount] = useState(0);
    const [percent, setPercent] = useState(0);
    const [autoApply, setAutoApply] = useState(false);

    const parentLookupRef = useRef(null);
    const receiptLookupRef = useRef(null);
    const amountRef = useRef(null);
    const percentRef = useRef(null);
    // Set while a field's exit check has its message up, so a Split pressed in the same click waits for it.
    const fieldCheckPendingRef = useRef(false);

    const authHeaders = useCallback(() => ({
        access_token: accessToken,
        Authorization: `Bearer ${accessToken}`,
    }), [accessToken]);

    // Field descriptors for the shared DynamicFromLookup component (SelectBox display mode).
    const parentLookupField = useMemo(() => ({
        name: "parentHeaderRowId",
        label: "Order #",
        tableName: "OMTRANSACTIONHEADER",
        ignoredblocalization: true,
        isLabelChanged: true,
        required: false,
        readOnly: parentLocked,
        lookupDisplayMode: DynamicLookupOptions.SelectBoxDisplayMode,
        layout: PARENT_ORDER_LOOKUP_LAYOUT,
    }), [parentLocked]);

    const receiptLookupField = useMemo(() => ({
        name: "receiptMethodRowId",
        label: "Receipt Mthd",
        tableName: "ACRECEIPTMETHODSETUP",
        ignoredblocalization: true,
        isLabelChanged: true,
        required: false,
        readOnly: false,
        lookupDisplayMode: DynamicLookupOptions.SelectBoxDisplayMode,
        layout: RECEIPT_METHOD_LOOKUP_LAYOUT,
    }), []);

    const resetState = useCallback(() => {
        setCurrent({});
        setParent(null);
        setParentLocked(false);
        setReceiptMethodRowId(null);
        setBasis(BASIS.CURRENT);
        setAmount(0);
        setPercent(0);
        setAutoApply(false);
    }, []);

    const closePopup = useCallback(() => {
        setShowPopup({ visible: false, headerRowId: null });
        resetState();
    }, [setShowPopup, resetState]);

    // Delphi clears both transfer fields and puts the cursor back after a refused value.
    const resetTransfer = useCallback((focusRef) => {
        setAmount(0);
        setPercent(0);
        focusEditor(focusRef);
    }, []);

    // Loads a parent-order snapshot via the same GetSplitDepositData method (Delphi GetParentData).
    // Note: changing the parent does not clear Amount/Percent (Delphi only clears them on Basis change).
    const selectParent = useCallback(async (parentRowId, lock = false) => {
        if (!hasGuid(parentRowId)) {
            setParent(null);
            setParentLocked(false);
            return;
        }
        if (parentRowId === headerRowId) {
            await alert(MESSAGES.SPLIT_DEPOSIT_PARENT_SAME_AS_CURRENT, MESSAGES.ERROR);
            setParent(null);
            return;
        }
        setLoading(true);
        try {
            const res = await axios.get(`${baseUrl()}/${routes.OM.GetSplitDepositData}`, {
                params: { headerRowId: parentRowId },
                headers: authHeaders(),
            });
            const data = res.data || {};
            if (data.isCommitted) {
                // Delphi ClearParentData + re-enable the picker: the user must choose another parent.
                await alert(MESSAGES.SPLIT_DEPOSIT_PARENT_COMMITTED, MESSAGES.ERROR);
                setParent(null);
                setParentLocked(false);
                return;
            }
            if (!data.hasRemaining) {
                // Delphi sets IsValid := false and FormPaint closes the dialog ("Force the user to exit the screen").
                await alert(MESSAGES.SPLIT_DEPOSIT_PARENT_NO_DEPOSIT, MESSAGES.ERROR);
                closePopup();
                return;
            }
            setParent(data);
            setParentLocked(lock);
            // Delphi GetParentData: the parent's own deposit method fills Receipt Mthd only when it is still empty.
            setReceiptMethodRowId((prev) => (hasGuid(prev) ? prev : (data.receiptMethodRowId || null)));
        } catch (err) {
            await alert(err?.response?.data?.message || err?.message || MESSAGES.SPLIT_DEPOSIT_LOAD_PARENT_FAILED, MESSAGES.ERROR);
            setParent(null);
        } finally {
            setLoading(false);
        }
    }, [authHeaders, headerRowId, closePopup]);

    const loadData = useCallback(async () => {
        if (!visible || !hasGuid(headerRowId)) return;
        setLoading(true);
        try {
            const dataRes = await axios.get(`${baseUrl()}/${routes.OM.GetSplitDepositData}`, {
                params: { headerRowId },
                headers: authHeaders(),
            });

            const data = dataRes.data || {};
            setCurrent(data);
            setAutoApply(data.autoApply === true);
            // Delphi FormShow: Receipt Mthd opens on the OM setup default only.
            setReceiptMethodRowId(data.setupReceiptMethodRowId || null);

            // Order produced by a split links to its parent — load and lock it (Delphi FormShow). selectParent
            // reports a committed parent and unlocks the picker.
            if (hasGuid(data.parentHeaderRowId)) {
                await selectParent(data.parentHeaderRowId, true);
            } else {
                setParent(null);
                setParentLocked(false);
            }
        } catch (err) {
            await alert(err?.response?.data?.message || err?.message || MESSAGES.SPLIT_DEPOSIT_LOAD_FAILED, MESSAGES.ERROR);
            closePopup();
        } finally {
            setLoading(false);
        }
    }, [visible, headerRowId, authHeaders, closePopup, selectParent]);

    useEffect(() => {
        if (visible) loadData();
    }, [visible, headerRowId]);

    // Basis for conversion: Current Trans Total or Parent Remaining (Delphi edtAmountTransferExit / edtPercentTransferExit).
    const conversionBase = basis === BASIS.CURRENT ? (current.transTotal || 0) : (parent?.remaining || 0);

    const onBasisChanged = useCallback((value) => {
        setBasis(value);
        setAmount(0);
        setPercent(0);
    }, []);

    // Recompute the paired field only for user edits (e.event set) — a programmatic value change
    // (from the paired setter) must not recompute back, or rounding would drift Amount/Percent.
    const onAmountChanged = useCallback((e) => {
        if (!e.event) return;
        const amt = Number(e.value) || 0;
        setAmount(amt);
        setPercent(conversionBase === 0 ? 0 : round2((amt / conversionBase) * 100));
    }, [conversionBase]);

    const onPercentChanged = useCallback((e) => {
        if (!e.event) return;
        const pct = Number(e.value) || 0;
        setPercent(pct);
        setAmount(round2((conversionBase * pct) / 100));
    }, [conversionBase]);

    // Shows a refused field value's message, then clears both transfer fields and refocuses (Delphi OnExit + abort).
    const refuseField = useCallback(async (message, focusRef) => {
        fieldCheckPendingRef.current = true;
        try {
            await alert(message, MESSAGES.ERROR);
        } finally {
            fieldCheckPendingRef.current = false;
        }
        resetTransfer(focusRef);
    }, [resetTransfer]);

    // Delphi edtAmountTransferExit. The editor's own value is read because onValueChanged has only just queued
    // the state update when focus leaves. With no parent picked yet the Split check reports the missing parent.
    const onAmountFocusOut = useCallback(async (e) => {
        const amt = Number(e.component.option("value")) || 0;
        if (parent && amt > (parent.remaining || 0)) {
            await refuseField(MESSAGES.SPLIT_DEPOSIT_AMOUNT_EXCEEDS_PARENT, amountRef);
        } else if (amt < 0) {
            await refuseField(MESSAGES.SPLIT_DEPOSIT_AMOUNT_NEGATIVE, amountRef);
        }
    }, [parent, refuseField]);

    // Delphi edtPercentTransferExit.
    const onPercentFocusOut = useCallback(async (e) => {
        const pct = Number(e.component.option("value")) || 0;
        if (pct > 100) {
            await refuseField(MESSAGES.SPLIT_DEPOSIT_PERCENT_OVER_100, percentRef);
        } else if (pct < 0) {
            await refuseField(MESSAGES.SPLIT_DEPOSIT_PERCENT_NEGATIVE, percentRef);
        }
    }, [refuseField]);

    // Mirror of Delphi ValidData; returns the refusal message, or null when the transfer may go ahead.
    const getValidationError = useCallback(() => {
        if (!parent || !hasGuid(parent.headerRowId)) return MESSAGES.SPLIT_DEPOSIT_PARENT_REQUIRED;
        if (amount === 0) return MESSAGES.SPLIT_DEPOSIT_AMOUNT_REQUIRED;
        if (amount < 0) return MESSAGES.SPLIT_DEPOSIT_AMOUNT_NEGATIVE;
        if (!hasGuid(receiptMethodRowId)) return MESSAGES.SPLIT_DEPOSIT_PAY_METHOD_REQUIRED;
        if (amount > (parent.remaining || 0)) return MESSAGES.SPLIT_DEPOSIT_AMOUNT_EXCEEDS_PARENT;
        if (percent > 100) return MESSAGES.SPLIT_DEPOSIT_PERCENT_OVER_100;
        if (percent < 0) return MESSAGES.SPLIT_DEPOSIT_PERCENT_NEGATIVE;
        // Delphi: TotalAmount - (Amount + TotalPayment), TotalPayment being SPOM_CALCULATETRANSTOTALS @OutARPayments.
        const newBalanceDue = round2((current.transTotal || 0) - (amount + (current.arPayments || 0)));
        if (newBalanceDue < 0) {
            return MESSAGES.SPLIT_DEPOSIT_BALANCE_DUE_NEGATIVE.replace("{0}", formatNumber(newBalanceDue, MESSAGE_CURRENCY_FORMAT));
        }
        return null;
    }, [parent, amount, percent, receiptMethodRowId, current]);

    const handleSplit = useCallback(async () => {
        if (fieldCheckPendingRef.current) return;

        const validationError = getValidationError();
        if (validationError) {
            // Delphi btnSplitClick: a refused split clears Amount/Percent and returns to Amount.
            await alert(validationError, MESSAGES.ERROR);
            resetTransfer(amountRef);
            return;
        }

        setLoading(true);
        try {
            const res = await axios.post(
                `${baseUrl()}/${routes.OM.ExecuteSplitDeposit}`,
                {
                    headerRowId: current.headerRowId,
                    parentHeaderRowId: parent.headerRowId,
                    receiptMethodRowId,
                    amount,
                    transferPercent: percent,
                    autoApply,
                },
                { headers: { ...authHeaders(), "Content-Type": "application/json" } }
            );
            // The transfer is in. resultdata = false means Auto Apply did not complete: Delphi shows that message
            // and still closes with mrOK. Otherwise btnSplitClick just closes — no success message.
            const result = res.data || {};
            if (result.resultdata === false && result.message) {
                await alert(result.message, MESSAGES.WARNING);
            }
            closePopup();
            if (onSuccess) onSuccess();
        } catch (err) {
            await alert(err?.response?.data?.message || err?.message || MESSAGES.SPLIT_DEPOSIT_FAILED, MESSAGES.ERROR);
        } finally {
            setLoading(false);
        }
    }, [getValidationError, resetTransfer, current, parent, receiptMethodRowId, amount, percent, autoApply, authHeaders, closePopup, onSuccess]);

    const groupBox = { flex: 1, borderRadius: 6, padding: 12 };
    const fieldStack = { display: "flex", flexDirection: "column", gap: 10 };

    const readOnlyNumber = (label, value) => (
        <NumberBox label={label} value={value ?? 0} readOnly format={CURRENCY_FORMAT} stylingMode="outlined" labelMode="static" />
    );

    // Split action + Auto Apply live on the popup header, right-aligned (Delphi Split / Auto Apply).
    const headerToolbarItems = [
        {
            location: "after",
            toolbar: "top",
            locateInMenu: "never",
            widget: "dxCheckBox",
            options: { text: "Auto Apply Cash Upon Split", value: autoApply, onValueChanged: (e) => setAutoApply(e.value) },
        },
        {
            location: "after",
            toolbar: "top",
            locateInMenu: "never",
            widget: "dxButton",
            options: { text: "Split", type: "default", stylingMode: "contained", onClick: handleSplit },
        },
    ];

    return (
        <Popup
            visible={visible}
            onHiding={closePopup}
            title={MESSAGES.SPLIT_DEPOSIT_TITLE}
            width={760}
            height={540}
            showCloseButton
            dragEnabled
            resizeEnabled
            toolbarItems={headerToolbarItems}
            wrapperAttr={{ class: "split-deposit-popup" }}
        >
            <LoadPanel
                visible={loading}
                shading
                shadingColor="rgba(0,0,0,0.2)"
                showIndicator
                showPane
                hideOnOutsideClick={false}
                container=".split-deposit-popup .dx-overlay-content"
                position={{ my: "center", at: "center", of: ".split-deposit-popup .dx-overlay-content" }}
            />
            <div style={{ padding: "4px 8px" }}>
                {/* Parent / Current side by side (Delphi uOMD0035) */}
                <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
                    <fieldset className="col-fieldset" style={groupBox}>
                        <legend className="list-title">Parent</legend>
                        <div style={fieldStack}>
                            <DynamicFromLookup
                                fieldRef={parentLookupRef}
                                name={parentLookupField.name}
                                required={false}
                                field={parentLookupField}
                                formFields={{ parentHeaderRowId: parent?.headerRowId ?? "" }}
                                layout={parentLookupField.layout}
                                editMode={false}
                                onValueChanged={(e) => selectParent(e.value, false)}
                            />
                            {readOnlyNumber("Trans Total", parent?.transTotal)}
                            {readOnlyNumber("Avl Deposit", parent?.remaining)}
                            {readOnlyNumber("Balance", parent?.balanceDue)}
                            {readOnlyNumber("Percent", parent?.percent)}
                        </div>
                    </fieldset>

                    <fieldset className="col-fieldset" style={groupBox}>
                        <legend className="list-title">Current</legend>
                        <div style={fieldStack}>
                            <TextBox label="Order #" value={current.orderNo || ""} readOnly stylingMode="outlined" labelMode="static" />
                            {readOnlyNumber("Trans Total", current.transTotal)}
                            {readOnlyNumber("Avl Deposit", current.remaining)}
                            {readOnlyNumber("Balance", current.balanceDue)}
                            {readOnlyNumber("Percent", current.percent)}
                        </div>
                    </fieldset>
                </div>

                {/* Transfer */}
                <fieldset className="col-fieldset" style={{ borderRadius: 6, padding: 12 }}>
                    <legend className="list-title">Transfer</legend>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                        <SelectBox
                            label="Basis"
                            dataSource={BASIS_OPTIONS}
                            value={basis}
                            valueExpr="id"
                            displayExpr="name"
                            stylingMode="outlined"
                            labelMode="static"
                            onValueChanged={(e) => onBasisChanged(e.value)}
                        />
                        <NumberBox
                            ref={amountRef}
                            label="Amount"
                            value={amount}
                            format={CURRENCY_FORMAT}
                            stylingMode="outlined"
                            labelMode="static"
                            onValueChanged={onAmountChanged}
                            onFocusOut={onAmountFocusOut}
                        />
                        <NumberBox
                            ref={percentRef}
                            label="Percent"
                            value={percent}
                            format={CURRENCY_FORMAT}
                            stylingMode="outlined"
                            labelMode="static"
                            onValueChanged={onPercentChanged}
                            onFocusOut={onPercentFocusOut}
                        />
                        <DynamicFromLookup
                            fieldRef={receiptLookupRef}
                            name={receiptLookupField.name}
                            required={false}
                            field={receiptLookupField}
                            formFields={{ receiptMethodRowId: receiptMethodRowId ?? "" }}
                            layout={receiptLookupField.layout}
                            editMode={false}
                            onValueChanged={(e) => setReceiptMethodRowId(e.value)}
                        />
                    </div>
                </fieldset>
            </div>
        </Popup>
    );
}

export default SplitDepositPopup;
